/**
 * Metadata Manager Page
 *
 * Purpose: Administrative interface for managing field definitions, fields, and views
 *          within the OrchestrateHQ AI platform. Supports GLOBAL tenant functionality
 *          and tenant-specific metadata management.
 *
 * Architecture: Uses SkeletonPage template with tabbed interface for different
 *              metadata management functions. Integrates with Auth service for
 *              tenant discovery and permission validation.
 *
 * Dependencies: React, Material-UI, SkeletonPage, MetadataService, AuthContext
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Alert, CircularProgress } from '@mui/material';
import {
  Settings as SettingsIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Storage as StorageIcon,
  ViewList as ViewListIcon,
  Category as CategoryIcon,
  Public as GlobalIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import SkeletonPage from '../../components/layout/SkeletonPage';
import { useAuth } from '../../context/AuthContext';
import FieldDefinitionManager from './MetadataManager/FieldDefinitionManager';
import FieldManager from './MetadataManager/FieldManager';
import ViewManager from './MetadataManager/ViewManager';
import { metadataService } from '../../services/metadataService';
import TenantSelector from '../../components/common/TenantSelector';
import { DismissibleBanner } from '../../components/common/DismissibleBanner';

// Styled components

const ContentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Interfaces
interface TenantInfo {
  tenant_id: string;
  name: string;
  is_global: boolean;
  can_edit: boolean;
}

interface MetadataManagerContext {
  current_tenant: TenantInfo;
  available_tenants: TenantInfo[];
  user_permissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab panel component
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`metadata-tabpanel-${index}`}
      aria-labelledby={`metadata-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `metadata-tab-${index}`,
    'aria-controls': `metadata-tabpanel-${index}`,
  };
};

/**
 * Metadata Manager Page Component
 *
 * Purpose: Central interface for metadata management with tenant context switching
 * Logic: Manages tabs, tenant context, and delegating to specific manager components
 */
// Map between tab names (URL) and tab indices
const TAB_NAMES = ['field-definitions', 'fields', 'views'] as const;
type TabName = (typeof TAB_NAMES)[number];

const tabNameToIndex = (name: string | null): number => {
  const idx = TAB_NAMES.indexOf(name as TabName);
  return idx >= 0 ? idx : 0;
};

const tabIndexToName = (index: number): TabName => {
  return TAB_NAMES[index] || 'field-definitions';
};

const MetadataManagerPage: React.FC = () => {
  const { state } = useAuth();
  const { user } = state;
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive tab and navigation context from URL params
  const activeTab = tabNameToIndex(searchParams.get('tab'));
  const urlModelId = searchParams.get('model');
  const fromFieldDefLabel = searchParams.get('from');

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<MetadataManagerContext | null>(null);
  const [switchingContext, setSwitchingContext] = useState(false);

  // Breadcrumbs for the page
  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'Metadata Manager', icon: <SettingsIcon fontSize="inherit" /> },
  ];

  /**
   * Load metadata manager context from API
   */
  const loadMetadataManagerContext = useCallback(
    async (explicitTenantId?: string, signal?: AbortSignal) => {
      try {
        setLoading(true);
        setError(null);

        // Check if request was cancelled
        if (signal?.aborted) {
          return;
        }

        // Get metadata manager context from API
        const response = await metadataService.getManagerContext(explicitTenantId);

        // Check again if request was cancelled before setting state
        if (!signal?.aborted) {
          if (response.data) {
            setContext(response.data);
          } else {
            throw new Error('No context data received from API');
          }
        }
      } catch (err) {
        // Only handle error if request wasn't cancelled
        if (!signal?.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load metadata manager context');
        }
      } finally {
        // Only set loading to false if request wasn't cancelled
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    []
  );

  // Load initial context - use main tenant context or URL parameter for testing
  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const testTenantId = urlParams.get('tenant_id');

      // Use main tenant context if no URL parameter is provided
      const mainTenantId = testTenantId || user?.activeTenant?.tenant_id;
      await loadMetadataManagerContext(mainTenantId, abortController.signal);
    };

    loadData();

    // Cleanup function to abort the request if component unmounts or dependencies change
    return () => {
      abortController.abort();
    };
  }, [loadMetadataManagerContext, user?.activeTenant?.tenant_id]);

  /**
   * Handle tenant context switch
   */
  const handleTenantSwitch = async (newTenantId: string) => {
    if (!context || newTenantId === context.current_tenant.tenant_id) {
      return;
    }

    try {
      setSwitchingContext(true);
      setError(null);

      // Switch context using API
      await metadataService.switchContext(newTenantId);

      // Update context locally to maintain the selected tenant
      const newTenant = context.available_tenants.find((t) => t.tenant_id === newTenantId);
      if (newTenant) {
        setContext({
          ...context,
          current_tenant: {
            tenant_id: newTenant.tenant_id,
            name: newTenant.name,
            is_global: newTenant.is_global,
            can_edit: newTenant.can_edit,
          },
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch tenant context');
    } finally {
      setSwitchingContext(false);
    }
  };

  /**
   * Handle tab change - updates URL params
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newParams = new URLSearchParams(searchParams);
    const tabName = tabIndexToName(newValue);
    if (tabName === 'field-definitions') {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tabName);
    }
    // Clear navigation context when manually switching tabs
    newParams.delete('model');
    newParams.delete('from');
    setSearchParams(newParams, { replace: true });
  };

  /**
   * Handle navigation to a specific view from the Fields tab
   */
  const handleNavigateToView = (_viewId: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'views');
    newParams.delete('model');
    newParams.delete('from');
    setSearchParams(newParams, { replace: false });
  };

  /**
   * Handle navigation to Fields tab with a specific model selected
   * Includes the field definition label for the back banner
   */
  const handleNavigateToFields = (modelId: string, fieldDefLabel?: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', 'fields');
    newParams.set('model', modelId);
    if (fieldDefLabel) {
      newParams.set('from', fieldDefLabel);
    }
    setSearchParams(newParams, { replace: false });
  };

  /**
   * Handle navigating back to Field Definitions tab (clears navigation context)
   */
  const handleBackToFieldDefinitions = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tab');
    newParams.delete('model');
    newParams.delete('from');
    setSearchParams(newParams, { replace: false });
  };

  // Loading state
  if (loading) {
    return (
      <SkeletonPage pageTitle="Metadata Manager" breadcrumbs={breadcrumbs}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </SkeletonPage>
    );
  }

  // Error state
  if (error || !context) {
    return (
      <SkeletonPage pageTitle="Metadata Manager" breadcrumbs={breadcrumbs}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Failed to load metadata manager'}
        </Alert>
      </SkeletonPage>
    );
  }

  const { current_tenant, available_tenants, user_permissions } = context;
  const canEdit = current_tenant.is_global
    ? user_permissions.can_edit_global
    : user_permissions.can_edit_tenant;

  return (
    <SkeletonPage pageTitle="Metadata Manager" breadcrumbs={breadcrumbs}>
      {/* Content Header with Tenant Selector */}
      <ContentHeader>
        <Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Manage metadata schemas, field definitions, and system configurations
          </Typography>
        </Box>

        {/* Tenant Selector - Top Right */}
        {user_permissions.can_switch_context && (
          <TenantSelector
            currentTenant={current_tenant.tenant_id}
            availableTenants={available_tenants}
            onTenantChange={handleTenantSwitch}
            disabled={switchingContext}
            loading={switchingContext}
            size="small"
            dataTestId="tenant-context-selector"
          />
        )}
      </ContentHeader>

      {/* Global Warning Banner */}
      {current_tenant.is_global && (
        <DismissibleBanner severity="warning" icon={<GlobalIcon />} useGlobalStyle>
          <Typography variant="subtitle1" component="div">
            You are managing GLOBAL metadata
          </Typography>
          <Typography variant="body2">
            Changes to GLOBAL metadata affect all tenants in the system. Please use caution when
            making modifications.
          </Typography>
        </DismissibleBanner>
      )}

      {/* Tab Interface */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="metadata manager tabs">
          <Tab
            label="Field Definitions"
            icon={<CategoryIcon />}
            iconPosition="start"
            {...a11yProps(0)}
          />
          <Tab label="Fields" icon={<StorageIcon />} iconPosition="start" {...a11yProps(1)} />
          <Tab label="Views" icon={<ViewListIcon />} iconPosition="start" {...a11yProps(2)} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <FieldDefinitionManager
          tenantContext={current_tenant}
          canEdit={canEdit}
          userPermissions={user_permissions}
          onNavigateToFields={handleNavigateToFields}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <FieldManager
          tenantContext={current_tenant}
          canEdit={canEdit}
          userPermissions={user_permissions}
          onNavigateToView={handleNavigateToView}
          preSelectedModelId={urlModelId}
          fromFieldDefLabel={fromFieldDefLabel}
          onBackToFieldDefinitions={handleBackToFieldDefinitions}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <ViewManager
          tenantContext={current_tenant}
          canEdit={canEdit}
          userPermissions={user_permissions}
        />
      </TabPanel>
    </SkeletonPage>
  );
};

export default MetadataManagerPage;
